import otel, { Context } from '@opentelemetry/api';
import { isEqual } from 'lodash';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import {
  AuthService,
  AuthServiceOptions,
  AuthState,
  newAuthService,
} from '@trp-ta-nitro/auth-browser-js';

export type AuthServiceContext = AuthService | null;
export const authServiceContext = React.createContext<AuthServiceContext | Error>(null);

export type AuthStateContext = AuthState | null;
export const authStateContext = React.createContext<AuthStateContext>(null);

export type AuthServiceOptionsProvider = () =>
  | AuthServiceOptions
  | Promise<AuthServiceOptions>;
export type AuthServiceOptionsSource =
  | AuthServiceOptions
  | Promise<AuthServiceOptions>
  | AuthServiceOptionsProvider;

const tracer = otel.trace.getTracer('nitro/auth-react');

/**
 * If authService is actually a service, do whatever operations need to be done to clean it up. This
 * keeps things like event handlers from sticking around and being triggered after the authService
 * is no longer being actively used.
 *
 * @param authService
 */
function cleanupAuthService(authService: AuthServiceContext | Error | null) {
  if (!authService || authService instanceof Error) {
    return;
  }
  authService.clearAuthStateListeners();
}

/**
 * Takes a source of options (an object or a function that returns an object) and returns auth
 * options as an object.
 *
 * @param optionsSource
 */
async function coerceOptions(
  optionsSource: AuthServiceOptionsSource,
): Promise<AuthServiceOptions> {
  if (typeof optionsSource === 'function') {
    optionsSource = optionsSource();
  }
  return optionsSource;
}

/**
 * Initializes the auth service provider by:
 *   - Instantiates a new auth service.
 *   - Registers setAuthState for changes to auth state.
 *   - Kicks off the verifyAuthentication() process.
 *
 * @param setAuthService
 * @param setAuthState
 * @param opts
 * @param context
 */
function initAuthService(
  setAuthService: (authService: AuthServiceContext) => void,
  setAuthState: (authState: AuthStateContext) => void,
  opts: AuthServiceOptions,
  context?: Context,
) {
  const activeContext = context ?? otel.context.active();

  try {
    const authService = newAuthService(opts);
    setAuthService(authService);
    authService.listenAuthState(setAuthState);
    const span = tracer.startSpan('verifyAuthentication()', undefined, activeContext);
    otel.context.with(otel.trace.setSpan(activeContext, span), () => {
      // the above listener should fire off the change in auth state
      authService
        .verifyAuthentication()
        .catch((err) => setAuthState(err))
        .finally(() => span.end());
    });
  } catch (err: any) {
    setAuthService(err);
  }
}

/**
 * Comparisons for effects are done by reference, not by value. This function changes that to be based on value.
 * Allows for effects that shouldn't be re-executed unless an actual value changes.
 *
 * @param value
 */
function memoizeComparison(value: any) {
  const ref = useRef();

  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

export interface Props {
  children?: ReactNode;
  context?: Context;
  options: AuthServiceOptionsSource;
}

/**
 * Facilitates all other auth components and hooks by setting up context for both an AuthService
 * and its associated authState.
 *
 * This component does not provide any enforcement, it exists to set up a single set of state for
 * auth management and relies on other components and hooks to provide access to that state and use
 * it. Because of this, the component should be added fairly early in the React component tree.
 *
 * The provider automatically calls the verifyAuthentication() function of the auth service once it
 * is available to set up and make auth state available, even if that auth state is not immediately
 * used. As with any AuthService usage, this might result in an immediate redirect if the user
 * is not logged in and requireAuthentication is true.
 *
 * All AuthService options are available via the `options` property. The property can be either
 * an object with the options, a Promise that resolves to the options, or a function that returns
 * either.
 *
 * @param props
 * @constructor
 */
const AuthServiceProvider: FC<Props> = (props) => {
  const { children, context, options } = props;
  const [authService, setAuthService] = useState<AuthServiceContext | Error>(null);
  const [authState, setAuthState] = useState<AuthStateContext>(null);

  useEffect(() => {
    cleanupAuthService(authService);
    coerceOptions(options).then((opts) => {
      initAuthService(setAuthService, setAuthState, opts, context);
    });
  }, [memoizeComparison(options)]);

  return (
    <authServiceContext.Provider value={authService}>
      <authStateContext.Provider value={authState}>{children}</authStateContext.Provider>
    </authServiceContext.Provider>
  );
};

export default AuthServiceProvider;
